import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { listClientUsers, updateUser } from "../../../api/chatbotApi";
import useAuth from "../../../hooks/useAuth";
import { isClientAdmin } from "../../../util/accessControl";
import { authStore } from "../../../util/authStore";

export default function ChatbotClientUserEditPage() {
  const { clientId, userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authorized = isClientAdmin(user, clientId);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setForm(null);
    setError("");
    if (!authorized) return () => { active = false; };
    listClientUsers(clientId).then((response) => {
      if (!Array.isArray(response?.data)) throw new Error("Invalid client access response.");
      const membership = response.data.find((item) => String(item.user_id ?? item.user?.id) === String(userId));
      if (!membership) throw new Error("User not found in this client.");
      const person = membership.user || {
        name: membership.user_name, gender: membership.user_gender, email: membership.user_email,
      };
      if (active) setForm({
        name: person.name || "", gender: person.gender ?? membership.user_gender ?? "", email: person.email || "",
      });
    }).catch((err) => {
      if (active) setError(err.response?.data?.detail || err.message || "Unable to load user.");
    });
    return () => { active = false; };
  }, [authorized, clientId, userId]);

  const save = async (event) => {
    event.preventDefault();
    const values = {
      name: form.name.trim(), gender: form.gender.trim() || null, email: form.email.trim(),
    };
    if (!values.name || !values.email) {
      setError("Name and email are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateUser(userId, values);
      if (String(userId) === String(user?.id)) await authStore.refresh(clientId);
      navigate(`/clients/${clientId}/users`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Unable to update user.");
    } finally {
      setSaving(false);
    }
  };

  if (!authorized) return <section className="rounded-xl bg-white p-8"><h1 className="text-xl font-bold">Access denied</h1><p>Only Super Admins and Client Admins can edit client users.</p></section>;

  return <section className="mx-auto max-w-2xl space-y-5">
    <Link to={`/clients/${clientId}/users`} className="text-indigo-700">Back to team</Link>
    <h1 className="text-2xl font-bold">Edit user</h1>
    <p className="text-sm text-slate-600">Name and email are shared across this user's clients. Client admins can edit accounts only when they administer all of the user's clients. Use Edit access on the team page to manage roles.</p>
    {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-rose-700">{error}</p>}
    {!form ? !error && <p role="status">Loading user...</p> : <form onSubmit={save} className="space-y-5 rounded-xl border bg-white p-6">
      <label className="block font-semibold" htmlFor="user-name">Name</label>
      <input id="user-name" required maxLength={200} value={form.name} disabled={saving} onChange={(event) => setForm({ ...form, name: event.target.value })} className="w-full rounded-lg border p-3" />
      <label className="block font-semibold" htmlFor="user-gender">Gender</label>
      <select id="user-gender" value={form.gender} disabled={saving} onChange={(event) => setForm({ ...form, gender: event.target.value })} className="w-full rounded-lg border p-3">
        <option value="">Not specified</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="nonbinary">Non-binary</option>
        <option value="other">Other</option>
        <option value="prefer_not_to_say">Prefer not to say</option>
      </select>
      <label className="block font-semibold" htmlFor="user-email">Email</label>
      <input id="user-email" required type="email" maxLength={200} value={form.email} disabled={saving} onChange={(event) => setForm({ ...form, email: event.target.value })} className="w-full rounded-lg border p-3" />
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="rounded-lg bg-cyan-800 px-4 py-2 font-semibold text-white">{saving ? "Saving..." : "Save user"}</button>
        <Link to={`/clients/${clientId}/users`} className="rounded-lg border px-4 py-2">Cancel</Link>
      </div>
    </form>}
  </section>;
}
